import { useState } from "react";
import { Info, Pencil, Settings2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { AssistantIdentity, Contact } from "@/types/contacts";
import { AssistantAvatar } from "@/components/agent/AssistantAvatar";
import { AssistantBusinessCard } from "@/components/agent/AssistantBusinessCard";
import { AssistantIdentityDialog } from "@/components/agent/AssistantIdentityDialog";
import { ChannelSettingsDialog } from "@/components/agent/ChannelSettingsDialog";
import { EmailSetupDialog } from "@/components/agent/EmailSetupDialog";
import { ConfigDialog } from "@/components/agent/ConfigDialog";
import { VoiceChip } from "@/components/agent/VoiceChip";
import { ChannelConnectDialog } from "@/components/contacts/ChannelConnectDialog";
import { AgentIncognitoIcon } from "@/components/icons/AgentIncognitoIcon";
import { assistantDisplayName } from "@/hooks/useAssistantIdentity";
import type { AgentSection } from "@/components/agent/AgentSurface";

/**
 * `AssistantHero` — the identity-first header of the Agent pane (redesign S1
 * §3.1) as a BUSINESS CARD (§3.1-bis, refinement #9), operator-shaped
 * (2026-07-27):
 *  - a DARK title strip on top — small caps, centered, the rail's own
 *    incognito mark ("Assistente Pessoal Lexiun") — carrying the pane close
 *    at its right;
 *  - below it the card: identity column (avatar over name) on the left, the
 *    contact chips STACKED beside it, and the tiny ⓘ orientation note — one
 *    centered composition.
 *
 * Every editable facet has a PENCIL that opens its editor IN PLACE: the
 * avatar → the curated picker, the name → the rename field (both
 * `AssistantIdentityDialog`), each chip → its channel's settings dialog
 * (`ChannelSettingsDialog`, incl. the typed-phrase-gated disconnect). Still
 * exactly ONE close (`ownsClose`). Maximize is NOT here: it acts on the tab
 * area, so it lives on the tab strip.
 */
export function AssistantHero({
  identity,
  owner,
  onSelectSection,
  onIdentityChanged,
  onClose,
  initialConfigOpen,
}: {
  identity: AssistantIdentity | null;
  /** The §6.1 owner record — the e-mail dialog's {UserName} preview needs it. */
  owner: Contact | null;
  onSelectSection: (section: AgentSection) => void;
  /** Re-read identity after a dialog writes it. */
  onIdentityChanged: () => void;
  onClose: () => void;
  /** §4.5 alias — open the gear dialog on mount (a `?section=settings` deep
   *  link, whose tab S4 retired). */
  initialConfigOpen?: boolean;
}) {
  const { t } = useTranslation();
  const name = assistantDisplayName(identity);
  /** Which editor is open: an identity facet, a channel, a connect flow, or
   *  nothing. */
  const [editing, setEditing] = useState<
    | { kind: "avatar" }
    | { kind: "name" }
    | { kind: "channel"; channel: "email" | "telegram" | "whatsapp" }
    | { kind: "connect"; channel: "telegram" | "whatsapp" }
    | { kind: "config" }
    | null
  >(initialConfigOpen ? { kind: "config" } : null);

  return (
    <div
      data-testid="assistant-hero"
      className="flex shrink-0 flex-col overflow-hidden rounded-xl border border-cream-200/70 dark:border-coffee-800"
    >
      {/* Dark explainer strip (operator 2026-07-27): what this pane IS, in
          small caps, CENTERED, under the same mark the nav rail uses — so the
          rail item and the pane visibly name the same thing. Dark in BOTH
          themes (operator: centralize text, dark background). */}
      <div className="relative flex items-center justify-center gap-2 bg-coffee-800 px-8 py-1.5 text-cream-100 dark:bg-coffee-950">
        <AgentIncognitoIcon size={14} className="shrink-0" />
        <span className="min-w-0 truncate text-[10px] font-semibold uppercase tracking-widest">
          {t("agent.hero.subtitle")}
        </span>
        <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
          {/* The gear — what remains of Configurações (§3.4, S4): the
              cross-cutting residue only. Everything settings-near-function
              now lives on the thing it configures. */}
          <button
            type="button"
            data-testid="config-open"
            onClick={() => setEditing({ kind: "config" })}
            aria-label={t("agent.sections.settings")}
            title={t("agent.sections.settings")}
            className="rounded-md p-1 text-cream-400 transition-colors hover:bg-coffee-700 hover:text-white dark:hover:bg-coffee-800"
          >
            <Settings2 size={14} />
          </button>
          <button
            type="button"
            data-testid="focus-pane-close"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
            className="rounded-md p-1 text-cream-400 transition-colors hover:bg-coffee-700 hover:text-white dark:hover:bg-coffee-800"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* The card: identity column + contacts column, centered together over
          the soft gradient. Wraps to one centered column when the pane is
          narrow. */}
      <div className="flex w-full flex-wrap items-center justify-center gap-x-8 gap-y-2.5 bg-gradient-to-b from-white/80 via-cream-50/60 to-cream-100/50 px-6 py-3 dark:from-coffee-900/70 dark:via-coffee-900/50 dark:to-coffee-950/50">
        <div className="flex flex-col items-center text-center">
          <button
            type="button"
            data-testid="assistant-avatar-edit"
            onClick={() => setEditing({ kind: "avatar" })}
            aria-label={t("agent.hero.identity.avatarTitle")}
            title={t("agent.hero.identity.avatarTitle")}
            className="group relative"
          >
            <AssistantAvatar
              identity={identity}
              size={56}
              testid="assistant-avatar"
              className="shadow-sm ring-2 ring-gold-400/50 ring-offset-2 ring-offset-white dark:ring-gold-500/40 dark:ring-offset-coffee-900"
            />
            {/* Pencil badge: "this face is yours to change". */}
            <span
              aria-hidden
              className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-cream-200 bg-white text-coffee-500 shadow-sm transition-colors group-hover:text-coffee-800 dark:border-coffee-700 dark:bg-coffee-800 dark:text-cream-400 dark:group-hover:text-cream-100"
            >
              <Pencil size={10} />
            </span>
          </button>
          <button
            type="button"
            data-testid="assistant-name-edit"
            onClick={() => setEditing({ kind: "name" })}
            aria-label={t("agent.hero.identity.nameTitle")}
            title={t("agent.hero.identity.nameTitle")}
            className="group mt-2 flex max-w-full items-center gap-1.5"
          >
            <h2
              data-testid="assistant-name"
              className="truncate font-serif-display text-display-sm leading-tight text-coffee-900 dark:text-cream-100"
            >
              {name}
            </h2>
            <Pencil
              size={12}
              aria-hidden
              className="shrink-0 text-cream-500 transition-colors group-hover:text-coffee-600 dark:text-coffee-600 dark:group-hover:text-cream-300"
            />
          </button>
          {/* Voice — the LAST slice (§3.6). Renders ONLY when actionable; on an
              org with no curated voices it is absent, never a dead knob. */}
          <div className="mt-1.5">
            <VoiceChip />
          </div>
        </div>

        <AssistantBusinessCard
          assistant={identity}
          onSelectSection={onSelectSection}
          onEditChannel={(channel) => setEditing({ kind: "channel", channel })}
          onConnectChannel={(channel) => setEditing({ kind: "connect", channel })}
        />

        {/* Third column (operator 2026-07-27): a tiny, discrete "what is
            this" note — orientation for the first-visit lawyer, whisper-quiet
            for everyone else. */}
        <div className="flex max-w-[200px] items-start gap-1.5 text-left">
          <Info
            size={13}
            aria-hidden
            className="mt-0.5 shrink-0 text-cream-500 dark:text-coffee-600"
          />
          <p className="text-[11px] leading-snug text-coffee-500 dark:text-cream-500">
            {t("agent.hero.infoNote")}
          </p>
        </div>
      </div>

      {(editing?.kind === "avatar" || editing?.kind === "name") && (
        <AssistantIdentityDialog
          mode={editing.kind}
          assistant={identity}
          ownerName={owner?.displayName}
          onClose={() => setEditing(null)}
          onChanged={onIdentityChanged}
        />
      )}
      {editing?.kind === "channel" && editing.channel !== "email" && (
        <ChannelSettingsDialog
          channel={editing.channel}
          assistant={identity}
          onClose={() => setEditing(null)}
          onChanged={onIdentityChanged}
        />
      )}
      {editing?.kind === "channel" && editing.channel === "email" && (
        <EmailSetupDialog
          assistant={identity}
          owner={owner}
          onClose={() => setEditing(null)}
          onChanged={onIdentityChanged}
        />
      )}
      {editing?.kind === "config" && <ConfigDialog onClose={() => setEditing(null)} />}
      {editing?.kind === "connect" && (
        <ChannelConnectDialog
          channel={editing.channel}
          onClose={() => {
            setEditing(null);
            // The card's chips poll, but a completed connect deserves an
            // instant re-read rather than the next tick.
            onIdentityChanged();
          }}
        />
      )}
    </div>
  );
}
